/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable react/jsx-no-literals */

import { NCAudioProps } from 'shared-definitions/types'
import { useAudioChart } from 'shared-components/hooks/use-audio-chart'
import { useEffect } from 'react'
import styleUtils from 'shared-components/styles/util.module.css'
import styles from './NCAudioChart.module.css'
import ChartSelect from 'shared-components/ui/ChartSelect'
import ChartOriginHints from './ChartOriginHints'
import clsx from 'clsx'

const NCAudioChart: React.VFC<NCAudioProps> = ({ data: { chart } }) => {
  const {
    filteredModels,
    isLoading,
    chartUrl,
    scrollPosition,
    dropdownOpen,
    filteredModelMeasures,
    filterValue,
    selectedModel,
    selectedModelMeasure,
    ref,
    filterModels,
    handleComparisonChange,
    handleModelMeasureChange,
    updateLoadingState,
    resetFilter,
    updateDropdownVisibility,
  } = useAudioChart(chart)

  useEffect(() => {
    if (scrollPosition) {
      window.scrollTo(0, scrollPosition)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  return (
    <div ref={ref} className={clsx(styles.container, styleUtils.nc)}>
      <ChartOriginHints url={chart.api_url} />
      {isLoading && (
        <div className={styles.placeholder}>
          <p>{'Loading chart ...'}</p>
        </div>
      )}

      {/* The box is fixed rather than collapsed to `0` until the embed's `load` event. That
          event waits on every subresource the ClearBuy chart page pulls (Chart.js and its
          annotation plugin) even though the frame is drawn before them, so the old gate held a
          ready chart behind a placeholder AND shifted 470px of layout under the reader when it
          finally lifted. The placeholder now sits behind the embed instead of in front of it.
          Size lives in `.embed` only — the old `width`/`height` attributes said the same thing
          in px, and two sources for one box is how they drift apart. */}
      <object
        aria-label="Audio Chart"
        className={styles.embed}
        data={chartUrl}
        type="text/html"
        onLoad={() => {
          if (chartUrl) {
            updateLoadingState(false)
          }
        }}
      />
      {selectedModel?.id && (
        <ChartSelect
          label="Comparison model"
          selectedItemName={selectedModel?.name}
          items={filteredModels}
          onItemClick={item => void handleComparisonChange(item)}
          isItemSelected={item => selectedModel?.id === item.id}
          getItemName={item => item.product.name}
          filterValue={filterValue || ''}
          onFilterChange={filterModels}
          onResetFilter={resetFilter}
          isOpen={dropdownOpen && Boolean(selectedModel?.id)}
          toggleOpen={updateDropdownVisibility}
          emptyMessage="No matching model"
        />
      )}
      {selectedModelMeasure?.id && (
        <ChartSelect
          label="Listening mode"
          selectedItemName={selectedModelMeasure?.name}
          items={filteredModelMeasures}
          onItemClick={item => void handleModelMeasureChange(item)}
          isItemSelected={item => selectedModelMeasure?.id === item.id}
          getItemName={item => item.name}
          filterValue={filterValue || ''}
          onFilterChange={filterModels}
          onResetFilter={resetFilter}
          isOpen={dropdownOpen && Boolean(selectedModelMeasure?.id)}
          toggleOpen={updateDropdownVisibility}
          emptyMessage="No matching measure"
        />
      )}
    </div>
  )
}

export default NCAudioChart
